import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

// Hell/Dunkel/System ohne next-themes: Dessen Inline-Skript verstößt gegen die CSP
// (script-src 'self', worker/index.ts) und erzeugt unter React 19 eine Warnung. applyStoredTheme()
// läuft stattdessen aus dem Bundle vor dem ersten Rendern (main.tsx) — kein Aufblitzen.

export type Theme = "light" | "dark" | "system";
const KEY = "optitime.theme";
const media = () => window.matchMedia("(prefers-color-scheme: dark)");

function stored(): Theme {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system";
  }
}

function apply(resolved: "light" | "dark") {
  document.documentElement.classList.toggle("dark", resolved === "dark");
  document.documentElement.style.colorScheme = resolved;
}

const resolve = (theme: Theme, systemDark: boolean): "light" | "dark" =>
  theme === "system" ? (systemDark ? "dark" : "light") : theme;

export function applyStoredTheme() {
  apply(resolve(stored(), media().matches));
}

const ThemeContext = createContext<{ theme: Theme; resolvedTheme: "light" | "dark"; setTheme: (t: Theme) => void } | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(stored);
  const [systemDark, setSystemDark] = useState(() => media().matches);
  const resolvedTheme = resolve(theme, systemDark);

  // DOM nachziehen (externes System) — kein setState im Effekt.
  useEffect(() => apply(resolvedTheme), [resolvedTheme]);

  useEffect(() => {
    const mql = media();
    const onChange = () => setSystemDark(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* nur Komfort */
    }
    setThemeState(next);
  }, []);

  return <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme außerhalb von ThemeProvider");
  return ctx;
}
